import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Bus, Flights, Guest, Hotel, HotelVideos, Train } from '../../assests/ExportImageIcon'
import MainTab from '../../componants/mainTab/MainTab'
import DatePicker from '../../componants/datePicker/DatePicker'
import PopularDestination from '../popularDestination/PopularDestination'
import SpecialOffer from '../specialOffer/SpecialOffer'
import InfluencerReels from '../influencerReels/InfluencerReels'
import GetApp from '../getApp/GetApp'
import OurLatestBlog from '../ourLatestblog/OurLatestBlog'
import Modal from '../../componants/modal/Modal'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import { setHotelResults, setHotelSearchParams, setHotelSearching, resetHotelFilters } from '../../redux/hotelSearch/hotelSearchSlice'
import { defaultRoomGuests, occupancyFromRooms, occupancyTotals, resizeRoomGuests } from '../../utils/hotelOccupancy'

const BOOKING_TYPES = [
  { key: 'daily', label: 'Daily Booking' },
  { key: 'hourly', label: 'Hourly Booking' },
]

const CHILD_AGES = Array.from({ length: 13 }, (_, i) => String(i))
const MAX_ADULTS_PER_ROOM = 8
const MAX_CHILDREN_PER_ROOM = 4
const MAX_GUESTS_PER_ROOM = 12

const todayIso = () => new Date().toISOString().slice(0, 10)

const Stepper = ({ value, onMinus, onPlus, minusDisabled = false, plusDisabled = false }) => (
  <div className="hotelGuestsModal__stepper">
    <button type="button" className="hotelGuestsModal__stepBtn" onClick={onMinus} disabled={minusDisabled} aria-label="Decrease">−</button>
    <span className="hotelGuestsModal__stepValue">{value}</span>
    <button type="button" className="hotelGuestsModal__stepBtn" onClick={onPlus} disabled={plusDisabled} aria-label="Increase">+</button>
  </div>
)

const HotelMain = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const currency = useSelector((state) => state.profileSetup?.currency) || 'INR'
  const locationRef = useRef(null)
  const debounceRef = useRef(null)
  const [bookingType, setBookingType] = useState('daily')
  const [locationQuery, setLocationQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestionsOpenUp, setSuggestionsOpenUp] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [guestsOpen, setGuestsOpen] = useState(false)
  const [roomGuests, setRoomGuests] = useState(defaultRoomGuests)
  const [draftRooms, setDraftRooms] = useState(defaultRoomGuests)
  const [roomLimitNotes, setRoomLimitNotes] = useState({})

  const fetchSuggestions = useCallback((query) => {
    if (!query || query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    service
      .get(endpoints.hotel.citySuggestion, { location: query, page: 1 }, { silent: true })
      .then(({ data }) => {
        const list = data?.data || data?.results || data || []
        setSuggestions(Array.isArray(list) ? list : [])
        setShowSuggestions(true)
      })
      .catch(() => {
        setSuggestions([])
      })
  }, [])

  const handleLocationInput = (e) => {
    const value = e.target.value
    setLocationQuery(value)
    setSelectedLocation(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 400)
  }

  const handleSelectSuggestion = (item) => {
    setSelectedLocation(item)
    setLocationQuery(item.cityName || item.city_name || item.name || item.city || '')
    setSuggestions([])
    setShowSuggestions(false)
  }

  useEffect(() => {
    if (!showSuggestions) return
    const handleOutside = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [showSuggestions])

  // Flip the suggestions list above the field when there isn't enough room
  // below (e.g. the search bar sits near the bottom of the viewport after
  // scrolling) — otherwise it opens off-screen / gets clipped.
  useEffect(() => {
    if (!showSuggestions || !locationRef.current) return
    const PANEL_HEIGHT = 260
    const rect = locationRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    setSuggestionsOpenUp(spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow)
  }, [showSuggestions])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleCheckInChange = (value) => {
    setCheckIn(value)
    if (checkOut && checkOut < value) {
      setCheckOut('')
    }
  }

  const openGuestsModal = () => {
    setDraftRooms(roomGuests.map((room) => ({
      adult: room.adult,
      childAges: [...room.childAges],
    })))
    setRoomLimitNotes({})
    setGuestsOpen(true)
  }

  const setRoomNote = (roomIndex, message) => {
    setRoomLimitNotes((prev) => ({ ...prev, [roomIndex]: message }))
  }

  const updateRoomCount = (delta) => {
    setDraftRooms((prev) => resizeRoomGuests(prev, prev.length + delta))
  }

  const updateRoomAdults = (roomIndex, delta) => {
    setDraftRooms((prev) => prev.map((room, index) => {
      if (index !== roomIndex) return room
      const nextAdult = room.adult + delta
      if (delta > 0 && (room.adult >= MAX_ADULTS_PER_ROOM || nextAdult > MAX_ADULTS_PER_ROOM)) {
        setRoomNote(roomIndex, 'Maximum 8 adults can be added in a single room')
        return room
      }
      if (delta > 0 && nextAdult + room.childAges.length > MAX_GUESTS_PER_ROOM) {
        setRoomNote(roomIndex, 'Maximum 12 guests can be added in a single room')
        return room
      }
      setRoomNote(roomIndex, '')
      return { ...room, adult: Math.min(MAX_ADULTS_PER_ROOM, Math.max(1, nextAdult)) }
    }))
  }

  const updateRoomChildren = (roomIndex, delta) => {
    setDraftRooms((prev) => prev.map((room, index) => {
      if (index !== roomIndex) return room
      const nextCount = room.childAges.length + delta
      if (delta > 0 && (room.childAges.length >= MAX_CHILDREN_PER_ROOM || nextCount > MAX_CHILDREN_PER_ROOM)) {
        setRoomNote(roomIndex, 'Maximum 4 children can be added in a single room')
        return room
      }
      if (delta > 0 && room.adult + nextCount > MAX_GUESTS_PER_ROOM) {
        setRoomNote(roomIndex, 'Maximum 12 guests can be added in a single room')
        return room
      }
      setRoomNote(roomIndex, '')
      const childCount = Math.min(MAX_CHILDREN_PER_ROOM, Math.max(0, nextCount))
      const childAges = [...room.childAges]
      if (childCount > childAges.length) {
        while (childAges.length < childCount) childAges.push('')
      } else {
        childAges.length = childCount
      }
      return { ...room, childAges }
    }))
  }

  const updateChildAge = (roomIndex, childIndex, value) => {
    setDraftRooms((prev) => prev.map((room, index) => {
      if (index !== roomIndex) return room
      const childAges = [...room.childAges]
      childAges[childIndex] = value
      return { ...room, childAges }
    }))
  }

  const applyGuests = () => {
    const overLimit = draftRooms.some((room) => (
      room.adult > MAX_ADULTS_PER_ROOM
      || room.childAges.length > MAX_CHILDREN_PER_ROOM
      || room.adult + room.childAges.length > MAX_GUESTS_PER_ROOM
    ))
    if (overLimit) {
      showToast('Each room allows up to 8 adults, 4 children, and 12 guests', 'error')
      return
    }
    const missingAge = draftRooms.some((room) => room.childAges.some((age) => age === ''))
    if (missingAge) {
      showToast('Please select age for all children', 'error')
      return
    }
    setRoomGuests(draftRooms.map((room) => ({
      adult: room.adult,
      childAges: [...room.childAges],
    })))
    setGuestsOpen(false)
  }

  const handleSearchHotels = () => {
    if (!selectedLocation) {
      showToast('Please select a city from suggestions', 'error')
      return
    }
    if (!checkIn) {
      showToast('Please select check-in date', 'error')
      return
    }
    if (!checkOut) {
      showToast('Please select check-out date', 'error')
      return
    }
    const missingAge = roomGuests.some((room) => room.childAges.some((age) => age === ''))
    if (missingAge) {
      showToast('Please select age for all children', 'error')
      return
    }

    const cityName = selectedLocation.cityName || selectedLocation.city_name || selectedLocation.name || selectedLocation.city || ''
    const countryName = selectedLocation.countryName || selectedLocation.country_name || selectedLocation.country || ''

    const occupancy = occupancyFromRooms(roomGuests)
    const totals = occupancyTotals(occupancy)
    const payload = {
      city: cityName,
      country: countryName,
      checkin: checkIn,
      checkout: checkOut,
      occupancy,
      requiredCurrency: currency || 'INR',
    }

    dispatch(setHotelSearchParams({
      city: cityName,
      country: countryName,
      cityId: selectedLocation.id || '',
      countryId: selectedLocation.country_id || '',
      checkIn,
      checkOut,
      rooms: totals.rooms,
      adults: totals.adults,
      children: totals.children,
      childAges: totals.childAges,
      occupancy,
      bookingType,
      requiredCurrency: currency || 'INR',
    }))
    dispatch(setHotelResults(null))
    dispatch(resetHotelFilters())
    dispatch(setHotelSearching(true))
    navigate('/hotel-list')

    // Search runs in the background; the list page shows a loader
    // (driven by isSearching) until results land in the store.
    ;(async () => {
      try {
        const { data } = await service.post(endpoints.hotel.search, payload, { timeout: 45000, silent: true })
        const responseBody = data?.data ?? data
        console.log('hotel search response', responseBody)
        const normalizedResults = Array.isArray(responseBody)
          ? { session_id: null, next_token: '', data: responseBody }
          : {
              session_id: responseBody?.session_id ?? null,
              next_token: responseBody?.next_token ?? '',
              data: Array.isArray(responseBody?.data) ? responseBody.data : [],
            }
        dispatch(setHotelResults(normalizedResults))
      } catch {
        // error toast already shown by the service interceptor
      } finally {
        dispatch(setHotelSearching(false))
      }
    })()
  }

  const guestCount = roomGuests.reduce((sum, room) => sum + room.adult + room.childAges.length, 0)
  const guestsLabel = `${roomGuests.length} Room${roomGuests.length > 1 ? 's' : ''}, ${guestCount} Guest${guestCount > 1 ? 's' : ''}`

  return (
    <>
        <section className='banner'>
            <div className='videoPart'>
                <video className='promoBanner' autoPlay loop muted playsinline="" data-object-fit="cover" preload="metadata">
                    <source src={HotelVideos} type="video/mp4" />
                </video>
            </div>            
            <div className='bannerContent'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <h1>Book Your Hotel With Just One Click</h1>
                            <MainTab />
                        </div>
                        <div className='col-md-12'>
                            <div className='bookingAreaanner'>
                                {/* <div className='commonBookingTab'>
                                    {BOOKING_TYPES.map((tab) => (
                                        <div
                                            key={tab.key}
                                            className={`bookTab ${bookingType === tab.key ? 'active' : ''}`}
                                            onClick={() => setBookingType(tab.key)}
                                        >
                                            {tab.label}
                                        </div>
                                    ))}
                                </div> */}

                                <div className='hotelSearchArea mt-0'>
                                    <div className='hotelSearchUp'>
                                        <div className='hotelSearchField cmnBdr hotelLocationField' ref={locationRef}>
                                            <div className='hotelSearchInput'>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12Z" stroke="#6B7280" strokeWidth="1.5" />
                                                    <circle cx="12" cy="10" r="2.5" stroke="#6B7280" strokeWidth="1.5" />
                                                </svg>
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder="Where are you going?"
                                                        value={locationQuery}
                                                        onChange={handleLocationInput}
                                                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                                        autoComplete="off"
                                                    />
                                                </div>
                                            </div>
                                            {showSuggestions && suggestions.length > 0 && (
                                                <ul className={`hotelLocationSuggestions ${suggestionsOpenUp ? 'hotelLocationSuggestions--up' : ''}`}>
                                                    {suggestions.map((item, index) => (
                                                        <li
                                                            key={item.id || index}
                                                            className="hotelLocationSuggestions__item"
                                                            onClick={() => handleSelectSuggestion(item)}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12Z" stroke="#6B7280" strokeWidth="1.5" />
                                                                <circle cx="12" cy="10" r="2.5" stroke="#6B7280" strokeWidth="1.5" />
                                                            </svg>
                                                            <div>
                                                                <span className="hotelLocationSuggestions__city">
                                                                    {item.cityName || item.city_name || item.name || item.city || ''}
                                                                </span>
                                                                {(item.countryName || item.country_name || item.country) && (
                                                                    <span className="hotelLocationSuggestions__country">
                                                                        , {item.countryName || item.country_name || item.country}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        <div className='hotelSearchField cmnBdr'>
                                            <div className='hotelSearchInput'>
                                                <div>
                                                    <DatePicker
                                                        className='searchBarDateField'
                                                        min={todayIso()}
                                                        value={checkIn}
                                                        onChange={handleCheckInChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='hotelSearchField cmnBdr'>
                                            <div className='hotelSearchInput'>
                                                <div>
                                                    <DatePicker
                                                        className='searchBarDateField'
                                                        min={checkIn || todayIso()}
                                                        value={checkOut}
                                                        onChange={setCheckOut}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='hotelSearchField hotelGuestsField'>
                                            <div className='hotelSearchInput' onClick={openGuestsModal}>
                                                <img src={Guest} alt="Guests" />
                                                <div>
                                                    <button type="button" className="hotelGuestsTrigger">
                                                        {guestsLabel}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                          type='button'
                                          className='commonBtn'
                                          onClick={handleSearchHotels}
                                        >
                                          Search Hotels
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>            
        </section>

        <Modal open={guestsOpen} onClose={() => setGuestsOpen(false)} maxWidth={430} className="hotelGuestsModalBox">
            <div className="hotelGuestsModal">
                <div className="hotelGuestsModal__row">
                    <span className="hotelGuestsModal__label">Room</span>
                    <Stepper
                        value={draftRooms.length}
                        onMinus={() => updateRoomCount(-1)}
                        onPlus={() => updateRoomCount(1)}
                    />
                </div>

                <div className="hotelGuestsModal__rooms">
                {draftRooms.map((room, roomIndex) => {
                    const guestCount = room.adult + room.childAges.length
                    const limitNote = roomLimitNotes[roomIndex]
                      || (room.adult >= MAX_ADULTS_PER_ROOM ? 'Maximum 8 adults can be added in a single room' : '')
                      || (room.childAges.length >= MAX_CHILDREN_PER_ROOM ? 'Maximum 4 children can be added in a single room' : '')
                      || (guestCount >= MAX_GUESTS_PER_ROOM ? 'Maximum 12 guests can be added in a single room' : '')
                    return (
                    <div className="hotelGuestsModal__room" key={`room-${roomIndex}`}>
                        <div className="hotelGuestsModal__roomTitle">Room {roomIndex + 1}</div>
                        <div className="hotelGuestsModal__row">
                            <span className="hotelGuestsModal__label">Adults</span>
                            <Stepper
                                value={room.adult}
                                onMinus={() => updateRoomAdults(roomIndex, -1)}
                                onPlus={() => updateRoomAdults(roomIndex, 1)}
                                minusDisabled={room.adult <= 1}
                            />
                        </div>
                        <div className="hotelGuestsModal__row">
                            <div>
                                <span className="hotelGuestsModal__label">Children</span>
                                <span className="hotelGuestsModal__hint">0 - 12 Years Old</span>
                            </div>
                            <Stepper
                                value={room.childAges.length}
                                onMinus={() => updateRoomChildren(roomIndex, -1)}
                                onPlus={() => updateRoomChildren(roomIndex, 1)}
                                minusDisabled={room.childAges.length <= 0}
                            />
                        </div>
                        {limitNote ? (
                            <p className="hotelGuestsModal__limit">{limitNote}</p>
                        ) : null}
                        {room.childAges.length > 0 && (
                            <>
                                <p className="hotelGuestsModal__childNote">
                                    Please provide right number of children along with their right age for best options and prices.
                                </p>
                                <div className="hotelGuestsModal__ages">
                                    <div className="hotelGuestsModal__agesTitle">Age of Children</div>
                                    {room.childAges.map((age, childIndex) => (
                                        <div className="hotelGuestsModal__ageRow" key={`room-${roomIndex}-child-${childIndex}`}>
                                            <span className="hotelGuestsModal__ageLabel">Child {childIndex + 1}</span>
                                            <select
                                                value={age}
                                                onChange={(e) => updateChildAge(roomIndex, childIndex, e.target.value)}
                                                aria-label={`Child ${childIndex + 1} age`}
                                            >
                                                <option value="">Select</option>
                                                {CHILD_AGES.map((option) => (
                                                    <option key={option} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    )
                })}
                </div>

                <div className="hotelGuestsModal__footer">
                    <button type="button" className="hotelGuestsModal__apply" onClick={applyGuests}>
                        APPLY
                    </button>
                </div>
            </div>
        </Modal>

        <PopularDestination />

        <SpecialOffer />

        <InfluencerReels />

        <GetApp />

        <OurLatestBlog />
    </>
  )
}

export default HotelMain
